import {
  fetchQueryResponse,
  setCloningValidationMessages,
  updateFieldsForPropertyResponse,
} from 'modules/queryBuilder/legacyQueryBuilder/actions';
import { parseSearchQueryResponseData } from 'modules/queryBuilder/legacyQueryBuilder/helpers';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectIsAllCombinatorSearchQueryFeatureFlagOn } from 'core/userContext';
import isEmpty from 'lodash/isEmpty';
import QueryBuilder from 'modules/queryBuilder/legacyQueryBuilder';
import { WORKFLOW_TRIGGERS } from 'modules/workflows/constants';
import {
  getIsJourneyExitEventsEnabled,
  getIsYesNoSplitDataToggleEnabled,
  getStartNodeTriggerDataType,
  getStartNodeTriggerEventName,
  getStartNodeTriggerNodeClass,
} from 'modules/workflows/ducks';
import {
  INDETERMINABLE_ORIGIN_TRIGGERS,
  UNFILTERABLE_TRIGGERS,
} from 'modules/workflows/utils/nodeUtils/fieldsMatchUtils/fieldMatchUtils.constants';
import { JourneyDataType, WhatsAppDataType } from 'modules/domains';
import { USER } from 'constants/esDataTypes';
import { useWhatsAppMessageChannelFeature } from 'utils/hooks/featureFlags';
import {
  formValidationMessagesForLists,
  formatFields,
  prepareClonedData,
} from './FieldsMatchQueryBuilder.utils';
import {
  FIELDS_MATCH_EMPTY_CRITERIA_PROPERTY,
  FIELDS_MATCH_EMPTY_SEARCH_GROUP,
  FIELDS_MATCH_EMPTY_SEARCH_GROUP_WITH_ALL,
  FIELDS_MATCH_EMPTY_SEARCH_QUERY,
  FIELDS_QUERY_TYPE,
} from './constants';

import { FieldsMatchQueryBuilderWrapper } from './FieldsMatchEditor.styles';
import { filterMappingsByTriggeringEvent } from './util';

export const FIELDS_QUERY_BUILDER_TEST_ID = 'fields-match-query-builder';

// type Props = {
//   /** [PRO-3634] `displayContent` convention for localization. Turn text to translate into a localization en-US.json */
//   content?: {
//     groupCombinatorPrependText: string,
//   },
//   fieldMappings: {},
//   /** a map of {[property]: [filteredCriteriaProperty]} that will remove a criteria proprety from the key'd property. Currently singular */
//   filteredFieldsByPropertyMap: {},
//   /** Saved fields match node data */
//   nodeData: {},
//   /** if match triggering event fields is toggled on */
//   isMatchTriggeringEventFields: boolean,
//   /** read only view prop */
//   isReadOnly: boolean,
// };

// [PRO-3634] `displayContent` convention for localization. Turn text to translate into a localization en-US.json
export const displayContent = {
  groupCombinatorPrependText: 'Find contacts that meet',
};

const API_CALL_PROPERTIES = [{ text: 'API Call', value: USER }];

export const FieldsMatchQueryBuilder = (
  {
    content = displayContent,
    fieldMappings = {},
    nodeData = {},
    isMatchTriggeringEventFields = false,
    clonedFromNodeData,
    hasCloningErrors = false,
    isReadOnly = false,
    filteredFieldsByPropertyMap = {},
  } /*: Props */,
) => {
  const dispatch = useDispatch();
  const isJourneyExitEventsEnabled = useSelector(getIsJourneyExitEventsEnabled);
  const isYesNoSplitDataToggleEnabled = useSelector(getIsYesNoSplitDataToggleEnabled);
  const { isWhatsAppEnabled: isWhatsAppMessageChannelEnabled } = useWhatsAppMessageChannelFeature();

  const startNodeNodeClass = useSelector(getStartNodeTriggerNodeClass);
  const startNodeDataType = useSelector(getStartNodeTriggerDataType);
  const startNodeEventName = useSelector(getStartNodeTriggerEventName);
  const isAllEmptySearchQuery = useSelector(selectIsAllCombinatorSearchQueryFeatureFlagOn);
  const isAPICall = startNodeNodeClass === WORKFLOW_TRIGGERS.RECEIVED_API;

  const shouldFilterFieldsAndProperties =
    isYesNoSplitDataToggleEnabled &&
    isMatchTriggeringEventFields &&
    ![...INDETERMINABLE_ORIGIN_TRIGGERS, ...UNFILTERABLE_TRIGGERS].includes(startNodeNodeClass);

  const isAddedItemToCart = startNodeNodeClass === WORKFLOW_TRIGGERS.ADDED_ITEM_TO_CART;
  const requiredDataType = isAPICall ? USER : startNodeDataType;

  const filteredFieldMappings = useMemo(
    () =>
      shouldFilterFieldsAndProperties
        ? filterMappingsByTriggeringEvent({
            mappings: fieldMappings,
            dataType: requiredDataType,
            eventName: startNodeEventName,
            nodeClass: isAddedItemToCart ? startNodeNodeClass : '',
          })
        : fieldMappings,
    [
      fieldMappings,
      isAddedItemToCart,
      shouldFilterFieldsAndProperties,
      startNodeDataType,
      startNodeEventName,
      startNodeNodeClass,
    ],
  );

  // If triggeringEvent radio is selected, disable but still show "user" property
  // (this may be deprecated when YesNoSplitDataToggle is globally enabled)
  const disabledProperties = !isAPICall && isMatchTriggeringEventFields ? [USER] : [];

  const journeyExitEventsHidden = isJourneyExitEventsEnabled ? [] : [JourneyDataType.JourneyExit];

  const whatsAppHiddenEvents = isWhatsAppMessageChannelEnabled
    ? []
    : Object.values(WhatsAppDataType);

  const hiddenProperties = [...journeyExitEventsHidden, ...whatsAppHiddenEvents];

  const hiddenSectionsOnDropdown = isWhatsAppMessageChannelEnabled ? [] : ['Whats App'];

  // Opposite of hiddenProperties
  // for example, if we pass ['customEvent'], user can only select "Custom Event" as a property.
  // is an array for future-proofing, but currently used to filter down to a single property.
  // see usage in src/modules/queryBuilder/legacyQueryBuilder/components/propertyDropdown/index.js
  const restrictToProperties = shouldFilterFieldsAndProperties ? [requiredDataType] : [];

  useEffect(() => {
    const formattedFieldMappings = formatFields(filteredFieldMappings);
    dispatch(updateFieldsForPropertyResponse(formattedFieldMappings));

    // WHAT A HACKY THING TO DO, but...
    // the <QueryBuilder /> component initializes itself with an empty block.
    // without this timeout, the dispatch fires before the that init happens
    // and we end up with the node's imported info AND an additional empty block.
    // when all we want is the imported info
    setTimeout(() => {
      // parseSearchQueryResponseData is a helper function provided by the queryBuilder module that
      // provides the initial query node data specifically formatted for the queryBuilder's redux state
      const parsed = parseSearchQueryResponseData(nodeData);

      // the way to provide init data to the queryBuilder component is by storing things in its redux store...
      // but we only need to store the data into redux, if the node comes in with saved search queries,
      // otherwise we want it to initialize with an empty query

      if (isEmpty(parsed?.searchQueries) && !isEmpty(clonedFromNodeData)) {
        const parsedClonedData = parseSearchQueryResponseData(clonedFromNodeData);

        // which campaigns and lists, etc, are not available to select? warn them
        const validationMessages = formValidationMessagesForLists(parsedClonedData);
        dispatch(setCloningValidationMessages(validationMessages));
        // prep clonedData so that all searchQuery.isClonedData=true so the UI can know.
        // also removes values for all lists, campagins, message channels, messages types, etc so the user can't save a non-existent value
        const preparedClonedData = prepareClonedData(parsedClonedData);
        dispatch(fetchQueryResponse(preparedClonedData));
      } else if (parsed.searchQueries) {
        dispatch(fetchQueryResponse(parsed));
      }
    });
  }, [clonedFromNodeData, filteredFieldMappings]);

  // We pass a custom set of properties if API call is selected and match triggering event fields is toggled on.
  // By setting to undefined, we leave the properties dropdown up to the PropertyDropdown component's default.
  const shouldShowAPICall =
    isYesNoSplitDataToggleEnabled && isMatchTriggeringEventFields && isAPICall;
  const properties = shouldShowAPICall ? API_CALL_PROPERTIES : undefined;
  const emptySearchGroup = isAllEmptySearchQuery
    ? FIELDS_MATCH_EMPTY_SEARCH_GROUP_WITH_ALL
    : FIELDS_MATCH_EMPTY_SEARCH_GROUP;

  return (
    <FieldsMatchQueryBuilderWrapper
      data-test={FIELDS_QUERY_BUILDER_TEST_ID}
      aria-disabled={isReadOnly}
      isReadOnly={isReadOnly}
    >
      <QueryBuilder
        type={FIELDS_QUERY_TYPE}
        disabledProperties={disabledProperties}
        emptyCriteriaProperty={FIELDS_MATCH_EMPTY_CRITERIA_PROPERTY}
        emptySearchGroup={emptySearchGroup}
        emptySearchQuery={FIELDS_MATCH_EMPTY_SEARCH_QUERY}
        filteredFieldsByPropertyMap={filteredFieldsByPropertyMap}
        groupCombinatorPrependText={content.groupCombinatorPrependText}
        hasCloningErrors={hasCloningErrors}
        hiddenPropertySections={hiddenSectionsOnDropdown}
        hiddenProperties={hiddenProperties}
        hideGroupsOnSingle={true}
        isReadOnly={isReadOnly}
        properties={properties}
        restrictToProperties={restrictToProperties}
        showCustomDateTextInput={true}
        showFieldTextInput={shouldShowAPICall}
        showStateButtons={false}
        useCastTypeField={true}
      />
    </FieldsMatchQueryBuilderWrapper>
  );
};
